import Layout from "../components/Layout.tsx";
import { useEffect, useState, useRef } from "react";
import { useRoute, useLocation } from "wouter";
import { RiDownloadLine } from "@remixicon/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/Table.tsx";
import { Button } from "../components/Button.tsx";
import { Badge } from "../components/Badge.tsx";
import { ProgressStepper, StatusLine, LoadingCard, useElapsedTimer } from "../components/RunProgress.tsx";
import { ApiError, fetchDryRunData, confirmDryRun, fetchRunDetails, downloadDryRunModuleExport } from "../services/ApiService.ts";
import { DryRunResponse, DryRunModuleData } from "../types.ts";
import { STAMGEGEVENS_CHANGED_FIELD_LABELS, MODULE_LABELS, parseUtcDate } from "../utils.ts";
import { toast } from "react-hot-toast";
import { isReadOnly } from "../config.ts";

/* ───────────── constants ───────────── */

const BOEKINGEN_MODULES = ["verkoop", "inkoop", "loon"];
const STAMGEGEVENS_MODULES = ["debiteuren", "grootboeken", "kostenplaatsen"];

const ACTION_DISPLAY: Record<string, { label: string; color: "emerald" | "blue" }> = {
  CREATED: { label: "Wordt aangemaakt", color: "emerald" },
  UPDATED: { label: "Wordt bijgewerkt", color: "blue" },
};

const DRY_RUN_STEPS = ["Opstarten", "Ophalen", "Voltooid"];

type Phase = "scheduled" | "fetching" | "completed" | "partial_success" | "failed" | "cancelled";

const PHASE_INFO: Record<Phase, { label: string; description: string }> = {
  scheduled: { label: "Opstarten", description: "Het proces wordt opgestart..." },
  fetching: { label: "Gegevens ophalen", description: "Data wordt opgehaald uit Easyflex..." },
  completed: { label: "Voltooid", description: "Alle gegevens zijn opgehaald" },
  partial_success: { label: "Deels geslaagd", description: "Niet alle gegevens konden worden opgehaald" },
  failed: { label: "Mislukt", description: "Er is een fout opgetreden" },
  cancelled: { label: "Geannuleerd", description: "De proefdraai is geannuleerd" },
};

const PHASE_TO_STEP: Record<Phase, number> = {
  scheduled: 0,
  fetching: 1,
  completed: 2,
  partial_success: 2,
  failed: -1,
  cancelled: -1,
};

function isJournaalpostModule(mod: DryRunModuleData): boolean {
  return BOEKINGEN_MODULES.includes(mod.module);
}

function getLoonPeriodFromBdatum(bdatum: unknown): string {
  return String(bdatum ?? "").slice(0, 7); // "YYYY-MM"
}

function getModuleJournaalpostCount(mod: DryRunModuleData): number | null {
  if (mod.module === "loon") {
    if (mod.data.length === 0) return null;
    const keys = new Set(mod.data.map((row) => `${row.identificatie}::${getLoonPeriodFromBdatum(row.bdatum)}`));
    return keys.size;
  }

  return mod.journaalpostCount;
}

function getModulePrimaryCount(mod: DryRunModuleData): number {
  const journaalpostCount = getModuleJournaalpostCount(mod);
  if (isJournaalpostModule(mod) && journaalpostCount != null) {
    return journaalpostCount;
  }

  return mod.recordCount;
}

function getModuleCountLabel(mod: DryRunModuleData): string {
  const count = getModulePrimaryCount(mod);

  if (isJournaalpostModule(mod)) {
    return count === 1 ? "journaalpost" : "journaalposten";
  }

  return count === 1 ? "item" : "items";
}

/* ───────────── sub-components ───────────── */

function ModuleCard({
  mod,
  label,
  isExpanded,
  onClick,
}: {
  mod: DryRunModuleData;
  label: string;
  isExpanded: boolean;
  onClick: () => void;
}) {
  const hasData = mod.recordCount > 0;
  const journaalpostCount = getModuleJournaalpostCount(mod);
  const primaryCount = getModulePrimaryCount(mod);

  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left rounded-xl border p-4 transition-all duration-200
        ${isExpanded
          ? "border-blue-300 bg-blue-50/50 ring-1 ring-blue-200 shadow-sm"
          : hasData
            ? "border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm"
            : "border-gray-100 bg-gray-50/50 hover:border-gray-200"
        }
      `}
    >
      <div className="text-sm text-gray-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-semibold tabular-nums ${hasData ? "text-gray-900" : "text-gray-300"}`}>
          {primaryCount}
        </span>
        {!hasData && <Badge color="zinc">geen data</Badge>}
      </div>
      <div className="mt-1 text-xs text-gray-500">{getModuleCountLabel(mod)}</div>
      {hasData && journaalpostCount != null && (
        <div className="mt-1 text-xs text-gray-400">
          {mod.recordCount} onderliggende journaalpostregels
        </div>
      )}
      {mod.cursorDate && (
        <div className="text-xs text-gray-400 mt-1">
          Vanaf {parseUtcDate(mod.cursorDate).toLocaleString("nl-NL")}
        </div>
      )}
    </button>
  );
}

const PAGE_SIZE = 100;
const STICKY_HEADER_CLASS = "sticky top-0 z-10 bg-gray-50";

function isChangedFieldList(value: unknown): value is Array<{ field: string; old?: unknown; new?: unknown }> {
  return Array.isArray(value) && value.every((item) => item != null && typeof item === "object" && "field" in item);
}

function formatPreviewValue(value: unknown): string {
  if (value == null || value === "") {
    return "-";
  }
  if (Array.isArray(value) || typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function renderPreviewCell(
  col: string,
  value: unknown,
  changedFields?: Array<{ field: string; old?: unknown; new?: unknown }>,
) {
  if (col === "action" && value != null && ACTION_DISPLAY[String(value)]) {
    return <Badge color={ACTION_DISPLAY[String(value)].color}>{ACTION_DISPLAY[String(value)].label}</Badge>;
  }

  // Show inline change for columns that have a matching changed_fields entry
  const change = changedFields?.find((c) => c.field === col);
  if (change) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />
        <span className="text-gray-400">{formatPreviewValue(change.old)}</span>
        <span className="text-gray-400">&rarr;</span>
        <span className="font-medium text-gray-900">{formatPreviewValue(change.new)}</span>
      </span>
    );
  }

  return value != null ? formatPreviewValue(value) : <span className="text-gray-300">&mdash;</span>;
}

function getPreviewColumnLabel(col: string): string {
  if (col === "action") {
    return "Actie";
  }
  return STAMGEGEVENS_CHANGED_FIELD_LABELS[col] || col;
}

function ModuleDetailTable({ mod }: { mod: DryRunModuleData }) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");

  if (mod.data.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
        Geen gegevens beschikbaar voor deze module
      </div>
    );
  }

  const PREFERRED_COLUMN_ORDER = ["Code", "Name", "action"];
  const rawColumns = Object.keys(mod.data[0]).filter((col) => !col.startsWith("_") && col !== "changed_fields");
  const columns = [
    ...PREFERRED_COLUMN_ORDER.filter((col) => rawColumns.includes(col)),
    ...rawColumns.filter((col) => !PREFERRED_COLUMN_ORDER.includes(col)),
  ];

  const filteredRows = search.trim()
    ? mod.data.filter((row) => {
      const q = search.toLowerCase();
      return Object.values(row).some(
        (val) => formatPreviewValue(val).toLowerCase().includes(q),
      );
    })
    : mod.data;

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * PAGE_SIZE;
  const visibleRows = filteredRows.slice(start, start + PAGE_SIZE);

  return (
    <div>
      <div className="mb-2">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="Zoeken in alle kolommen..."
          className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
        />
      </div>
      {filteredRows.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-gray-400 text-sm border border-gray-200 rounded-lg">
          Geen resultaten voor &ldquo;{search}&rdquo;
        </div>
      ) : (
        <>
          <div className="max-h-[400px] overflow-auto rounded-lg border border-gray-200">
            <Table dense className="min-w-max overflow-x-visible">
              <TableHead>
                <TableRow>
                  <TableHeader className={`w-12 text-center ${STICKY_HEADER_CLASS}`}>#</TableHeader>
                  {columns.map((col) => (
                    <TableHeader key={col} className={STICKY_HEADER_CLASS}>
                      {getPreviewColumnLabel(col)}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, idx) => {
                  const changedFields = isChangedFieldList(row["changed_fields"]) ? row["changed_fields"] : undefined;
                  return (
                    <TableRow key={idx}>
                      <TableCell className="text-center text-gray-400 text-xs tabular-nums">{start + idx + 1}</TableCell>
                      {columns.map((col) => (
                        <TableCell key={col}>
                          {renderPreviewCell(col, row[col], changedFields)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between px-1 py-2 text-xs text-gray-500">
            <span>
              {filteredRows.length === mod.data.length
                ? <>{start + 1}&ndash;{Math.min(start + PAGE_SIZE, filteredRows.length)} van {filteredRows.length} rijen</>
                : <>{filteredRows.length} van {mod.data.length} rijen</>
              }
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Vorige
                </button>
                <span className="px-2 tabular-nums">
                  {safePage + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={safePage >= totalPages - 1}
                  className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Volgende
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ───────────── main component ───────────── */

const DryRunPreview = () => {
  const [, params] = useRoute("/proef-synchronisatie/:runId");
  const [, setLocation] = useLocation();
  const runId = params?.runId ? parseInt(params.runId) : null;

  const [preview, setPreview] = useState<DryRunResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [exportingModule, setExportingModule] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase | null>(null);
  const [runDatetime, setRunDatetime] = useState<string | null>(null);
  const elapsed = useElapsedTimer(loading, runDatetime);

  // Poll for dry run completion
  const pollTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    let retries = 0;
    const MAX_RETRIES = 60;

    const loadPreview = async () => {
      try {
        const runDetails = await fetchRunDetails(runId);
        if (cancelled) return;
        setRunDatetime(runDetails.runDatetime);
        if (runDetails.dryRunConfirmed) setConfirmed(true);

        if (runDetails.state === "COMPLETED") {
          if (runDetails.result === "FAILED" || runDetails.result === "CANCELLED") {
            setPhase(runDetails.result === "CANCELLED" ? "cancelled" : "failed");
            setLoading(false);
            return;
          }
          const data = await fetchDryRunData(runId);
          if (!cancelled) {
            setPhase(runDetails.result === "PARTIAL_SUCCESS" ? "partial_success" : "completed");
            setPreview(data);
            setLoading(false);
          }
          return;
        }

        // Not completed yet — start polling
        const poll = async () => {
          if (cancelled || retries >= MAX_RETRIES) {
            if (!cancelled && retries >= MAX_RETRIES) {
              toast.error("Proefdraai duurt te lang, probeer het opnieuw");
              setPhase("failed");
              setLoading(false);
            }
            return;
          }
          retries++;
          try {
            const details = await fetchRunDetails(runId);
            if (cancelled) return;
            if (details.state === "SCHEDULED") {
              setPhase("scheduled");
              pollTimeout.current = setTimeout(poll, 3000);
              return;
            }
            if (details.state === "RUNNING") {
              setPhase("fetching");
              pollTimeout.current = setTimeout(poll, 3000);
              return;
            }
            if (details.result === "FAILED" || details.result === "CANCELLED") {
              setPhase(details.result === "CANCELLED" ? "cancelled" : "failed");
              setLoading(false);
              return;
            }
            const data = await fetchDryRunData(runId);
            if (!cancelled) {
              setPhase(details.result === "PARTIAL_SUCCESS" ? "partial_success" : "completed");
              setPreview(data);
              setLoading(false);
            }
          } catch {
            if (!cancelled) pollTimeout.current = setTimeout(poll, 3000);
          }
        };
        poll();
      } catch {
        if (!cancelled) {
          toast.error("Kon proefdraai niet laden");
          setPhase("failed");
          setLoading(false);
        }
      }
    };

    loadPreview();
    return () => { cancelled = true; clearTimeout(pollTimeout.current); };
  }, [runId]);

  const handleConfirm = async () => {
    if (!runId) return;
    setConfirming(true);
    try {
      const response = await confirmDryRun(runId);
      if (response.error) {
        if (response.error.includes("al bevestigd")) {
          setConfirmed(true);
          toast.error("Deze proefdraai is al bevestigd");
          return;
        }
        toast.error(`Synchronisatie starten mislukt: ${response.error}`);
        return;
      }
      toast.success("Synchronisatie gestart");
      setLocation("/synchronisaties");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "er is een onverwachte fout opgetreden";
      toast.error(`Synchronisatie starten mislukt: ${msg}`);
    } finally {
      setConfirming(false);
    }
  };

  const handleExport = async (module: string) => {
    if (!runId) return;

    setExportingModule(module);
    try {
      await downloadDryRunModuleExport(runId, module);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Onbekende fout";
      toast.error(`Exporteren mislukt: ${msg}`);
    } finally {
      setExportingModule(null);
    }
  };

  const totalRecords = preview?.modules.reduce((sum, m) => sum + m.recordCount, 0) ?? 0;
  const modulesWithData = preview?.modules.filter((m) => m.recordCount > 0).length ?? 0;

  /* ── loading / in-progress state ── */
  if (loading) {
    const stepIdx = phase ? PHASE_TO_STEP[phase] : -1;
    return (
      <Layout
        title=""
        description=""
        stepTitle="Proefdraai wordt uitgevoerd"
        showBackButton={false}
        showNextButton={false}
      >
        <div className="py-8 space-y-8">
          {phase ? (
            <>
              <ProgressStepper steps={DRY_RUN_STEPS} currentStep={stepIdx} failedStep={phase === "failed" ? 1 : undefined} />
              <StatusLine
                label={PHASE_INFO[phase].label}
                description={PHASE_INFO[phase].description}
                elapsed={elapsed}
                isActive={true}
              />
            </>
          ) : null}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <LoadingCard key={i} />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  /* ── failed / cancelled state ── */
  if (phase === "failed" || phase === "cancelled") {
    const isCancelled = phase === "cancelled";
    return (
      <Layout
        title=""
        description=""
        stepTitle={isCancelled ? "Proefdraai geannuleerd" : "Proefdraai mislukt"}
        showBackButton={false}
        showNextButton={false}
      >
        <div className="py-8 space-y-6">
          <ProgressStepper steps={DRY_RUN_STEPS} currentStep={-1} failedStep={1} />
          <div className="text-center space-y-3">
            <p className="text-sm font-medium text-gray-900">
              {isCancelled ? "De proefdraai is geannuleerd" : "De proefdraai is mislukt"}
            </p>
            <p className="text-sm text-gray-500">
              {isCancelled
                ? "Start een nieuwe proefdraai wanneer je verder wilt."
                : "Controleer de verbinding en probeer het opnieuw."}
            </p>
          </div>
          <div className="flex justify-center pt-2">
            <Button color="dark" href="/koppelingen">
              Terug naar koppelingen
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  /* ── no preview data ── */
  if (!preview) {
    return (
      <Layout
        title=""
        description=""
        stepTitle="Geen gegevens gevonden"
        showBackButton={false}
        showNextButton={false}
      >
        <div className="py-8 text-center space-y-3">
          <p className="text-sm text-gray-500">Geen data beschikbaar voor deze proefdraai.</p>
          <Button color="dark" href="/koppelingen">
            Terug
          </Button>
        </div>
      </Layout>
    );
  }

  /* ── completed preview ── */
  return (
    <Layout
      title=""
      description=""
      stepTitle={`${preview.connectionName} — ${parseUtcDate(preview.runDatetime).toLocaleString("nl-NL")}`}
      showBackButton={false}
      showNextButton={false}
    >
      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
        <Badge color="green">Voltooid</Badge>
        <span className="text-gray-400">|</span>
        <span className="text-gray-600">
          <strong className="text-gray-900">{totalRecords}</strong> items in <strong className="text-gray-900">{modulesWithData}</strong> modules
        </span>
        <span className="text-gray-400">|</span>
        <span className="text-gray-500 text-xs">Klik op een module om de gegevens te bekijken</span>
      </div>

      {/* Info message */}
      <div className="mb-6 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
        Deze gegevens zijn opgehaald uit Easyflex. Controleer de data en klik op <strong>Synchroniseren</strong> om deze te verwerken in Exact Online.
        <div className="mt-1">
          Bij verkoop, inkoop en loon toont de proefdraai journaalpostregels. Tijdens synchronisatie worden die regels gegroepeerd tot unieke journaalposten.
        </div>
      </div>

      {/* Module cards — Boekingen */}
      {(() => {
        const boekingen = preview.modules.filter((m) => BOEKINGEN_MODULES.includes(m.module));
        const stamgegevens = preview.modules.filter((m) => STAMGEGEVENS_MODULES.includes(m.module));
        // Modules that don't fit either group
        const other = preview.modules.filter(
          (m) => !BOEKINGEN_MODULES.includes(m.module) && !STAMGEGEVENS_MODULES.includes(m.module),
        );

        const renderGroup = (label: string, modules: DryRunModuleData[]) => {
          if (modules.length === 0) return null;
          return (
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{label}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {modules.map((mod) => (
                  <ModuleCard
                    key={mod.module}
                    mod={mod}
                    label={MODULE_LABELS[mod.module] || mod.module}
                    isExpanded={expandedModule === mod.module}
                    onClick={() => setExpandedModule(expandedModule === mod.module ? null : mod.module)}
                  />
                ))}
              </div>
            </div>
          );
        };

        return (
          <>
            {renderGroup("Stamgegevens", stamgegevens)}
            {renderGroup("Boekingen", boekingen)}
            {renderGroup("Overig", other)}
          </>
        );
      })()}

      {/* Expanded module detail */}
      {expandedModule && (() => {
        const mod = preview.modules.find((m) => m.module === expandedModule);
        if (!mod) return null;
        const journaalpostCount = getModuleJournaalpostCount(mod);
        const primaryCount = getModulePrimaryCount(mod);

        return (
          <div className="mb-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                {MODULE_LABELS[expandedModule] || expandedModule}
              </h3>
              <div className="flex items-center gap-3">
                <div className="text-right text-xs">
                  <div className="text-gray-500">{primaryCount} {getModuleCountLabel(mod)}</div>
                  {mod.recordCount > 0 && journaalpostCount != null && (
                    <div className="text-gray-500">{mod.recordCount} onderliggende regels</div>
                  )}
                </div>
                {mod.recordCount > 0 && (
                  <Button
                    color="light"
                    onClick={() => handleExport(mod.module)}
                    disabled={exportingModule === mod.module}
                  >
                    <RiDownloadLine data-slot="icon" />
                    {exportingModule === mod.module ? "Bezig..." : "Exporteren"}
                  </Button>
                )}
              </div>
            </div>
            {mod.recordCount > 0 && journaalpostCount != null && (
              <p className="text-xs text-gray-400">
                {mod.recordCount} onderliggende journaalpostregels
              </p>
            )}
            <ModuleDetailTable mod={mod} />
          </div>
        );
      })()}

      {/* Confirm button */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button color="dark" href="/synchronisaties">
          Terug
        </Button>
        <div className="flex items-center gap-3">
          {confirmed && (
            <span className="text-sm text-green-700">
              Deze proefdraai is al bevestigd en gesynchroniseerd.
            </span>
          )}
          {!confirmed && (
            <Button color="primary" onClick={() => setShowConfirmDialog(true)} disabled={confirming || isReadOnly}>
              {confirming ? "Bezig..." : "Synchroniseren"}
              <div className="relative group ml-1">
                <span className="w-4 h-4 inline-flex items-center justify-center rounded-full border border-white/40 text-white/70 text-[10px] cursor-default">i</span>
                <div className="absolute bottom-full right-0 mb-3 w-56 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity">
                  Alle opgehaalde data voor alle {modulesWithData} modules ({totalRecords} items) wordt gesynchroniseerd naar Exact Online.
                </div>
              </div>
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation dialog */}
      {showConfirmDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowConfirmDialog(false)}
        >
          <div
            className="bg-white rounded-lg max-w-sm w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-2">Synchronisatie bevestigen</h3>
            <p className="text-sm text-gray-600 mb-6">
              Alle opgehaalde data voor alle {modulesWithData} modules ({totalRecords} items) wordt gesynchroniseerd naar Exact Online. Weet je het zeker?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                disabled={confirming}
              >
                Annuleren
              </button>
              <Button
                color="primary"
                onClick={async () => {
                  setShowConfirmDialog(false);
                  await handleConfirm();
                }}
                disabled={confirming}
              >
                {confirming ? "Bezig..." : "Bevestigen"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default DryRunPreview;
